import { isNft, isProgrammable } from "@metaplex-foundation/js";
import type {
  BurnInstructionAccounts,
  BurnInstructionArgs,
  TransferInstructionAccounts,
  TransferInstructionArgs,
} from "@metaplex-foundation/mpl-token-metadata";
import { createBurnInstruction, createTransferInstruction } from "@metaplex-foundation/mpl-token-metadata";
import { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { PublicKey, SystemProgram, SYSVAR_INSTRUCTIONS_PUBKEY } from "@solana/web3.js";

import { getTokenRecordPda } from "../../models/metaplex";
import type { NFTWithTokenAccount } from "../../models/nfts/nfts";
import { findAssociatedTokenAddressSync } from "../../utils/tokenInfo";

export const createMetaplexNftTransferIx = (
  nft: NFTWithTokenAccount,
  amount: number,
  toAddress: PublicKey,
  fromAddress: PublicKey,
) => {
  const tokenAddress = new PublicKey(nft.associatedTokenAccount.pubkey);
  const toToken = findAssociatedTokenAddressSync(nft.address, new PublicKey(toAddress));

  const accounts: TransferInstructionAccounts = {
    token: new PublicKey(nft.associatedTokenAccount.pubkey),
    tokenOwner: fromAddress,
    destination: toToken,
    destinationOwner: new PublicKey(toAddress),
    mint: nft.address,
    metadata: nft.metadataAddress,
    edition: isNft(nft) ? nft.edition.address : undefined,
    ownerTokenRecord: isProgrammable(nft) ? getTokenRecordPda(nft.address, tokenAddress) : undefined,
    destinationTokenRecord: isProgrammable(nft) ? getTokenRecordPda(nft.address, toToken) : undefined,
    authority: fromAddress,
    payer: fromAddress,
    systemProgram: SystemProgram.programId,
    sysvarInstructions: SYSVAR_INSTRUCTIONS_PUBKEY,
    splTokenProgram: TOKEN_PROGRAM_ID,
    splAtaProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
  };
  const transferArgs: TransferInstructionArgs = {
    transferArgs: {
      __kind: "V1",
      amount,
      authorizationData: null,
    },
  };

  return createTransferInstruction(accounts, transferArgs);
};

export const createMetaplexNftBurnIx = (nft: NFTWithTokenAccount, amount: number, owner: PublicKey) => {
  const collectionMetadata = nft.collectionNFT?.metadataAddress;
  const metadata = nft.metadataAddress;
  const edition = isNft(nft) ? nft.edition.address : undefined;
  const mint = nft.address;
  const token = new PublicKey(nft.associatedTokenAccount.pubkey);
  const tokenRecord = isProgrammable(nft) ? getTokenRecordPda(nft.address, token) : undefined;

  const accounts: BurnInstructionAccounts = {
    authority: owner,
    collectionMetadata,
    metadata,
    edition,
    mint,
    token,
    tokenRecord,
    systemProgram: SystemProgram.programId,
    sysvarInstructions: SYSVAR_INSTRUCTIONS_PUBKEY,
    splTokenProgram: TOKEN_PROGRAM_ID,
  };
  const burnArgs: BurnInstructionArgs = {
    burnArgs: {
      __kind: "V1",
      amount,
    },
  };
  return createBurnInstruction(accounts, burnArgs);
};
